import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { ReactComponent as InfoIcon } from '../../../assets/images/svg/info-filled-blue.svg';
import Button from '../../Button/Button';
import Paragraph from '../../Paragraph/Paragraph';
import themeColors from '../../../assets/styles/themeColors';
import { variables } from '../../../assets/styles/variables';
import BasicAccountRestristionsModal from '../../Modals/BasicAccountRestrictionsModal/BasicAccountRestrictionsModal';
import IraUtmaAccountRestrictionModal from '../../Modals/IraUtmaAccountRestrictionModal/IraUtmaAccountRestrictionModal';
import NonUSAccountRestrictionModal from '../../Modals/NonUSAccountRestrictionModal/NonUSAccountRestrictionModal';
import SellFromHomeLockedModal from '../../Modals/SellWizard/SellFromHomeLockedModal';
import SellFromHomeWizard from '../../Modals/SellWizard/SellFromHomeWizard';
import SellOrStoreChoiceModal from '../../Modals/SellOrStoreChoiceModal/SellOrStoreChoiceModal';
import MailInStorageWizard from '../../Modals/MailInStorage/MailInStorageWizard';
import PendingAccountVerificationModal from '../../Modals/PendingAccountVerificationModal/PendingAccountVerificationModal';
import useSellFromHomeEligibility from '../../../util/hooks/useSellFromHomeEligibility';
import useSfhGtmHook from '../../../util/hooks/useSfhGtmHook';
import {
  SFH_CTA_PORTFOLIO_CLICKED,
  SFH_VERIFY_ACCOUNT_CLICKED,
  SFH_VERIFY_ACCOUNT_DEFERRED,
} from '../../../constants/gtmEvents';
import {
  fireSfhInitiationEvent,
  resolveSfhAuthorization,
} from '../../../util/helpers/sfhGtmHelpers';
import { useSfhJourneyStartedEvent } from '../../../util/helpers/crmEventsHelper';
import {
  getCurrentAccount,
  selectDefaultMailingAddress,
  selectAccountUsersData,
} from '../../../store/selectors/accountSelectors';
import { selectAuthUser } from '../../../store/selectors/userSelectors';
import { selectMailInEnabled } from '../../../store/selectors/settingsSelectors';
import {
  accountTypeKeys,
  accountTypes,
} from '../../../util/enum/api/accountTypes';
import { parseEnumType } from '../../../util/helpers/enumMappers';
import { postCrmEventMailinStorageStarted } from '../../../store/actions/crmEvent/crmEventActions';
import { useSessionStorageState } from '../../../util/hooks/useSessionStorageState';
import {
  ADD_MAIL_IN_STORAGE_WIZARD,
  IS_ADD_MAIL_IN_WIZARD_OPEN,
} from '../../../constants/sessionStorage';
import { removeFromSessionStorage } from '../../../util/helpers/sessionStorageHelper';

const SellFromHomeCtaContainer = styled.div`
  width: ${({ width }) => (width ? `${width}px` : '100%')};
  display: flex;
  flex-direction: ${({ stacked }) => (stacked ? 'column' : 'row')};
  align-items: ${({ stacked }) => (stacked ? 'stretch' : 'center')};
  justify-content: space-between;
  gap: 16px;
  padding: 16px;
  border: 1px solid ${themeColors.colorBorderPrimary};
  border-radius: 6px;
  margin-bottom: ${({ stacked }) => (stacked ? `20px` : '0px')};
  && button {
    padding: 14px;
    font-weight: 600;
    div {
      margin-right: 4px;
    }
  }
`;

const SellFromHomeTextContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const SellFromHomeHeader = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
`;

const TitleText = styled(Paragraph)`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: 16px;
  font-weight: 700;
  line-height: 1;
  letter-spacing: 0;
  color: ${themeColors.colorTextGreyDark};
`;

const SubtitleText = styled(Paragraph)`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 1;
  letter-spacing: 0;
  color: ${themeColors.colorLabel};
`;

const NewBadge = styled.span`
  background-color: ${themeColors.colorNewBadge};
  color: ${themeColors.colorBlack};
  font-size: 14px;
  font-weight: 600;
  border-radius: 4px;
  padding: 4px 8px;
  white-space: nowrap;
`;

const SellFromHomeCta = ({ stacked, width, onClick, onMailInClick }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [isChoiceModalOpen, setIsChoiceModalOpen] = useState(false);
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const [internalIsLockedOpen, setInternalIsLockedOpen] = useState(false);
  const {
    isGS,
    isNonUS,
    isBasicAccount,
    isIraOrUtmaAccount,
    isJointSubUser,
    isPendingAccount,
    isErrOrRaAccount,
    isSellFromHomeRestricted,
    isHealthCheckAccount,
    isSellFromHomeAllowedAccounts,
  } = useSellFromHomeEligibility();
  const { sfhGtmEvent } = useSfhGtmHook();
  const fireSfhJourneyStarted = useSfhJourneyStartedEvent();
  const defaultMailingAddress = useSelector(selectDefaultMailingAddress);
  const authUser = useSelector(selectAuthUser);
  const accountUsers = useSelector(selectAccountUsersData);
  const account = useSelector(getCurrentAccount);
  const mailInEnabled = useSelector(selectMailInEnabled);
  const isIRAAccount =
    parseEnumType(accountTypeKeys, account?.AccountType) === accountTypes.IRA;
  const mailInAllowed = mailInEnabled && !isIRAAccount;
  const [mailInWizardOpen, setMailInWizardOpen] = useSessionStorageState(
    ADD_MAIL_IN_STORAGE_WIZARD,
    false,
  );
  const isControlled = typeof onClick === 'function';
  const isMailInControlled = typeof onMailInClick === 'function';
  const isSfhLocked = isSellFromHomeRestricted || isHealthCheckAccount;

  if (!isGS || !(isSellFromHomeAllowedAccounts || isHealthCheckAccount)) {
    return null;
  }

  const startSellFromHome = () => {
    if (isControlled) {
      onClick();
      return;
    }
    sfhGtmEvent(SFH_CTA_PORTFOLIO_CLICKED, {
      authorization: resolveSfhAuthorization({ authUser, accountUsers }),
    });
    fireSfhJourneyStarted();
    if (isSfhLocked) {
      setInternalIsLockedOpen(true);
      return;
    }
    if (
      !isPendingAccount ||
      isErrOrRaAccount ||
      isIraOrUtmaAccount ||
      isJointSubUser ||
      isBasicAccount
    ) {
      fireSfhInitiationEvent(sfhGtmEvent, {
        isNonUS,
        isIraOrUtmaAccount,
        isJointSubUser,
        isBasicAccount,
        isErrOrRaAccount,
        country: defaultMailingAddress?.CountryIso3Code,
        authorization: resolveSfhAuthorization({ authUser, accountUsers }),
      });
    }
    setInternalIsOpen(true);
  };

  const startMailIn = () => {
    if (isMailInControlled) {
      onMailInClick();
      return;
    }
    dispatch(
      postCrmEventMailinStorageStarted({
        accountUid: account?.AccountUid,
      }),
    );
    setMailInWizardOpen(true);
  };

  const handleCtaClick = () => {
    if (mailInAllowed) {
      setIsChoiceModalOpen(true);
      return;
    }
    startSellFromHome();
  };

  const handleChoiceSellFromHome = () => {
    setIsChoiceModalOpen(false);
    startSellFromHome();
  };

  const handleChoiceMailIn = () => {
    setIsChoiceModalOpen(false);
    startMailIn();
  };

  return (
    <>
      <SellFromHomeCtaContainer stacked={stacked} width={width}>
        <SellFromHomeTextContent>
          <SellFromHomeHeader>
            <TitleText>{t('portfolio.sellFromHome.title')}</TitleText>
            <NewBadge>{t('portfolio.sellFromHome.badge')}</NewBadge>
          </SellFromHomeHeader>
          <SubtitleText color={themeColors.colorTextSecondary}>
            {t('portfolio.sellFromHome.subtitle')}
          </SubtitleText>
        </SellFromHomeTextContent>
        <Button
          variant="primary"
          textTransform="none"
          isWidthS={stacked}
          fontSize="16px"
          lockStyle={isSfhLocked && !mailInAllowed}
          onClick={handleCtaClick}
          data-cy="button-sell-from-home"
          label={t('portfolio.sellFromHome.cta')}
        />
      </SellFromHomeCtaContainer>

      <SellOrStoreChoiceModal
        isOpen={isChoiceModalOpen}
        close={() => setIsChoiceModalOpen(false)}
        onSellFromHomeClick={handleChoiceSellFromHome}
        onMailInClick={handleChoiceMailIn}
        showMailInOption={mailInAllowed}
        sellFromHomeLockStyle={isSfhLocked}
      />

      {!isMailInControlled && mailInWizardOpen && (
        <MailInStorageWizard
          isOpen={mailInWizardOpen}
          onClose={() => {
            removeFromSessionStorage(IS_ADD_MAIL_IN_WIZARD_OPEN);
            setMailInWizardOpen(false);
          }}
        />
      )}

      {!isControlled && (
        <>
          <SellFromHomeLockedModal
            isOpen={internalIsLockedOpen}
            close={() => setInternalIsLockedOpen(false)}
            title={
              isHealthCheckAccount
                ? t('portfolio.sellFromHome.healthCheck.title')
                : t('irs.ssn.error.SSNTooManyAttempts.title')
            }
            message={
              isHealthCheckAccount
                ? t('portfolio.sellFromHome.healthCheck.message')
                : t('irs.ssn.error.SSNTooManyAttempts.message')
            }
          />
          {isIraOrUtmaAccount && (
            <IraUtmaAccountRestrictionModal
              isOpen={internalIsOpen}
              close={() => setInternalIsOpen(false)}
            />
          )}
          {!isIraOrUtmaAccount && isJointSubUser && (
            <IraUtmaAccountRestrictionModal
              isOpen={internalIsOpen}
              close={() => setInternalIsOpen(false)}
              title={t('portfolio.sellFromHome.jointSubUser.title')}
              description={t('portfolio.sellFromHome.jointSubUser.description')}
              dataCyPrefix="button-sfh-joint-sub-user"
            />
          )}
          {!isIraOrUtmaAccount && !isJointSubUser && isBasicAccount && (
            <BasicAccountRestristionsModal
              isOpen={internalIsOpen}
              close={() => setInternalIsOpen(false)}
              title={t(
                'basicAccountRestrictions.verifyAccountSellFromHome.title',
              )}
              description={t(
                'basicAccountRestrictions.verifyAccountSellFromHome.description',
              )}
              icon={<InfoIcon width={48} height={48} />}
              showDoItLater
              onVerifyAccountClick={() =>
                sfhGtmEvent(SFH_VERIFY_ACCOUNT_CLICKED)
              }
              onDoItLaterClick={() => sfhGtmEvent(SFH_VERIFY_ACCOUNT_DEFERRED)}
            />
          )}
          {!isIraOrUtmaAccount &&
            !isJointSubUser &&
            !isBasicAccount &&
            isPendingAccount && (
              <PendingAccountVerificationModal
                isOpen={internalIsOpen}
                close={() => setInternalIsOpen(false)}
              />
            )}
          {!isIraOrUtmaAccount &&
            !isJointSubUser &&
            !isBasicAccount &&
            !isPendingAccount &&
            isNonUS && (
              <NonUSAccountRestrictionModal
                isOpen={internalIsOpen}
                close={() => setInternalIsOpen(false)}
              />
            )}
          {!isIraOrUtmaAccount &&
            !isJointSubUser &&
            !isBasicAccount &&
            !isPendingAccount &&
            !isNonUS && (
              <SellFromHomeWizard
                isOpen={internalIsOpen}
                onClose={() => setInternalIsOpen(false)}
              />
            )}
        </>
      )}
    </>
  );
};

SellFromHomeCta.propTypes = {
  stacked: PropTypes.bool,
  width: PropTypes.number,
  onClick: PropTypes.func,
  onMailInClick: PropTypes.func,
};

export default SellFromHomeCta;
